// Speech for coach replies, made reliable in Chrome. (v3, Oct 6 2026)
// Three Chrome faults are handled here, once, for every page:
//   1. one long utterance is cut off after about 15 seconds -> split into short pieces
//   2. queued utterances can be dropped or never start      -> speak one piece at a time
//   3. an utterance nothing references can be discarded     -> keep a reference until it ends
// A watchdog moves on if a piece never reports that it ended. The page's onstart
// fires with the first piece and its onend after the last. cancel() stops everything.
// If page code cancels speech while it is playing, the console names the caller.
// Loaded once from src/index.js.

const MAX_CHARS = 160;

export function splitForSpeech(text) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (!clean) return [];
  // End a sentence at . ! ? only when a space (or the end) follows, so
  // "Psalm 2:7" and "3.5" stay whole.
  const sentences = [];
  let start = 0;
  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i];
    if (ch === '.' || ch === '!' || ch === '?') {
      let j = i + 1;
      while (j < clean.length && '"\')]'.indexOf(clean[j]) !== -1) j++;
      if (j >= clean.length || clean[j] === ' ') {
        sentences.push(clean.slice(start, j).trim());
        start = j + 1;
        i = j;
      }
    }
  }
  if (start < clean.length) sentences.push(clean.slice(start).trim());

  const chunks = [];
  let cur = '';
  const flush = () => { if (cur) { chunks.push(cur); cur = ''; } };
  for (let s of sentences) {
    if (!s) continue;
    while (s.length > MAX_CHARS) {
      flush();
      let cut = Math.max(s.lastIndexOf(', ', MAX_CHARS), s.lastIndexOf('; ', MAX_CHARS), s.lastIndexOf(': ', MAX_CHARS));
      if (cut < MAX_CHARS * 0.4) cut = s.lastIndexOf(' ', MAX_CHARS);
      if (cut <= 0) cut = MAX_CHARS - 1;
      chunks.push(s.slice(0, cut + 1).trim());
      s = s.slice(cut + 1).trim();
    }
    if (!s) continue;
    if (cur && cur.length + 1 + s.length > MAX_CHARS) flush();
    cur = cur ? cur + ' ' + s : s;
  }
  flush();
  return chunks;
}

if (typeof window !== 'undefined' && window.speechSynthesis &&
    typeof window.SpeechSynthesisUtterance === 'function' && !window.speechSynthesis.__fhoSpeech) {
  const synth = window.speechSynthesis;
  const nativeSpeak = synth.speak.bind(synth);
  const nativeCancel = synth.cancel.bind(synth);
  let run = 0;         // goes up on every speak() and cancel(); older callbacks check it and stop
  let current = null;  // the piece being spoken, held so the browser cannot discard it
  let timer = null;

  const clear = () => { if (timer) { clearTimeout(timer); timer = null; } current = null; };

  synth.cancel = function () {
    if (current) console.debug('[FHO speech] cancel() called while speaking', new Error('caller').stack);
    run++;
    clear();
    return nativeCancel();
  };

  synth.speak = function (utterance) {
    const parts = splitForSpeech(utterance && typeof utterance.text === 'string' ? utterance.text : '');
    if (parts.length === 0) return nativeSpeak(utterance);
    const mine = ++run;
    clear();
    let i = 0;

    const next = () => {
      if (mine !== run) return;
      if (i >= parts.length) {
        clear();
        if (typeof utterance.onend === 'function') utterance.onend({ type: 'end', utterance: utterance });
        return;
      }
      const n = i++;
      const piece = new window.SpeechSynthesisUtterance(parts[n]);
      if (utterance.voice) piece.voice = utterance.voice;
      if (utterance.lang) piece.lang = utterance.lang;
      piece.rate = utterance.rate; piece.pitch = utterance.pitch; piece.volume = utterance.volume;

      let done = false;
      const advance = () => {
        if (done || mine !== run) return;
        done = true;
        if (timer) { clearTimeout(timer); timer = null; }
        next();
      };
      piece.onstart = (ev) => { if (n === 0 && mine === run && typeof utterance.onstart === 'function') utterance.onstart(ev); };
      piece.onend = advance;
      piece.onerror = (ev) => {
        if (done || mine !== run) return;
        const err = ev && ev.error;
        if (err === 'interrupted' || err === 'canceled') return;  // stopped on purpose
        console.warn('[FHO speech] piece ' + (n + 1) + ' of ' + parts.length + ' failed: ' + err);
        advance();
      };

      // Watchdog: if this piece never reports an end, move on rather than go silent.
      const rate = utterance.rate > 0 ? utterance.rate : 1;
      const budget = Math.round(parts[n].length * 95 / rate) + 4000;
      let waited = 0;
      const watch = (ms) => {
        timer = setTimeout(() => {
          if (done || mine !== run) return;
          waited += ms;
          if (synth.paused) return watch(2000);                           // the person paused it
          if (synth.speaking && waited < budget * 2) return watch(2000);   // still talking
          console.warn('[FHO speech] piece ' + (n + 1) + ' of ' + parts.length + ' did not finish; moving on');
          done = true;
          try { nativeCancel(); } catch (e) { /* ignore */ }
          next();
        }, ms);
      };

      current = piece;
      if (n === 0 && synth.paused) { try { synth.resume(); } catch (e) { /* ignore */ } }
      nativeSpeak(piece);
      watch(budget);
    };

    next();
  };

  synth.__fhoSpeech = 3;
  synth.__fhoChunked = true;
}
